/**
 * DevExtreme (esm/__internal/ui/collection/collection_widget.edit.strategy.js)
 * Version: 25.1.6
 * Build date: Mon Oct 13 2025
 *
 * Copyright (c) 2012 - 2025 Developer Express Inc. ALL RIGHTS RESERVED
 * Read about DevExtreme licensing here: https://js.devexpress.com/Licensing/
 */
import Class from "../../../core/class";
import domAdapter from "../../../core/dom_adapter";
import $ from "../../../core/renderer";
import { equalByValue } from "../../../core/utils/common";
import { isRenderer } from "../../../core/utils/type";
class EditStrategy {
  constructor(collectionWidget) {
    this._collectionWidget = collectionWidget;
  }
  _getItems() {
    const {
      items = []
    } = this._collectionWidget.option();
    return items;
  }
  getIndexByItemData(value) {
    return Class.abstract();
  }
  getItemDataByIndex(index) {
    return Class.abstract();
  }
  getKeysByItems(items) {
    return Class.abstract();
  }
  getItemsByKeys(keys, items) {
    return Class.abstract();
  }
  itemsGetter() {
    return Class.abstract();
  }
  getKeyByIndex(index) {
    const resultIndex = this._denormalizeItemIndex(index);
    return this.getKeysByItems([this.getItemDataByIndex(resultIndex)])[0];
  }
  _equalKeys(key1, key2) {
    if (this._collectionWidget._isKeySpecified()) {
      return equalByValue(key1, key2);
    }
    return key1 === key2;
  }
  beginCache() {
    this._cache = {};
  }
  endCache() {
    this._cache = null;
  }
  getIndexByKey(key) {
    return Class.abstract();
  }
  getNormalizedIndex(value) {
    if (this._isNode(value)) {
      return this._getNormalizedItemIndex(value);
    }
    if (this._isNormalizedItemIndex(value)) {
      return value;
    }
    if (this._isItemIndex(value)) {
      return this._normalizeItemIndex(value);
    }
    return this._normalizeItemIndex(this.getIndexByItemData(value));
  }
  getIndex(value) {
    if (this._isNode(value)) {
      return this._denormalizeItemIndex(this._getNormalizedItemIndex(value));
    }
    if (this._isNormalizedItemIndex(value)) {
      return this._denormalizeItemIndex(value);
    }
    if (this._isItemIndex(value)) {
      return value;
    }
    return this.getIndexByItemData(value);
  }
  getItemElement(value) {
    if (this._isNode(value)) {
      return $(value);
    }
    if (this._isNormalizedItemIndex(value)) {
      return this._getItemByNormalizedIndex(value);
    }
    if (this._isItemIndex(value)) {
      return this._getItemByNormalizedIndex(this._normalizeItemIndex(value));
    }
    const normalizedItemIndex = this._normalizeItemIndex(this.getIndexByItemData(value));
    return this._getItemByNormalizedIndex(normalizedItemIndex);
  }
  _isNode(el) {
    return domAdapter.isNode(el && isRenderer(el) ? el.get(0) : el);
  }
  deleteItemAtIndex(index) {
    return Class.abstract();
  }
  itemPlacementFunc(movingIndex, destinationIndex) {
    return this._itemsFromSameParent(movingIndex, destinationIndex) && movingIndex < destinationIndex ? "after" : "before";
  }
  moveItemAtIndexToIndex(movingIndex, destinationIndex) {
    return Class.abstract();
  }
  _isNormalizedItemIndex(index) {
    return "number" === typeof index && Math.round(index) === index;
  }
  _isItemIndex(index) {
    return Class.abstract();
  }
  _getNormalizedItemIndex(value) {
    return Class.abstract();
  }
  _normalizeItemIndex(index) {
    return Class.abstract();
  }
  _denormalizeItemIndex(index) {
    return Class.abstract();
  }
  _getItemByNormalizedIndex(index) {
    return Class.abstract();
  }
  _itemsFromSameParent(movingIndex, destinationIndex) {
    return Class.abstract();
  }
}
export default EditStrategy;